import { Injectable, inject } from '@angular/core';
import { ClinicalObservationsLocalStorageService } from './clinical-observations-ls.service';
import {
  ClinicalObservation,
  ClinicalObservationGroup,
  FormattedClinicalObservation,
} from './models/clinical-observation.interface';

@Injectable({
  providedIn: 'root',
})
export class ClinicalObservationsHelperService {
  private clinicalObsLocalStorageService = inject(ClinicalObservationsLocalStorageService);

  extractLatestClinicalObservationsByType(items: ClinicalObservation[]) {
    if (!items || items.length === 0) {
      return [];
    }
    const groupedClinicalObservations = items.reduce(
      (acc: Record<string, ClinicalObservation[]>, item: ClinicalObservation) => {
        const key = item.type;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      },
      {}
    );

    const latestClinicalObservations: ClinicalObservation[] = [];
    Object.keys(groupedClinicalObservations).forEach((key) => {
      const latestItem = groupedClinicalObservations[key].sort((a, b) => {
        return new Date(b.recorded_at).getTime() - new Date(a.recorded_at).getTime();
      })[0];

      latestClinicalObservations.push(latestItem);
    });

    return latestClinicalObservations;
  }

  calculateReferenceRangeStatus(item: ClinicalObservation) {
    if (
      !item.metadata ||
      !item.metadata.reference_value_min ||
      !item.metadata.reference_value_max
    ) {
      return null;
    }
    const value = Number(item.metadata.value);
    const referenceValueMin = Number(item.metadata.reference_value_min);
    const referenceValueMax = Number(item.metadata.reference_value_max);

    if (value < referenceValueMin) {
      return 'out-low';
    }
    if (value > referenceValueMax) {
      return 'out-high';
    }

    return 'in';
  }

  formatClinicalObservations(items: ClinicalObservation[]): FormattedClinicalObservation[] {
    return items.map((item) => {
      return {
        ...item,
        label: item.type,
        value: item.metadata.value,
        comment: item.metadata.comment,
        measurementUnit: item.metadata.measurement_unit,
        referenceRangeStatus: this.calculateReferenceRangeStatus(item),
      } as FormattedClinicalObservation;
    });
  }

  groupFormattedClinicalObservations(items: FormattedClinicalObservation[]) {
    const groupedByType = items.reduce(
      (acc, reading) => {
        if (!acc[reading.label]) {
          acc[reading.label] = [];
        }
        acc[reading.label].push(reading);
        return acc;
      },
      {} as Record<string, FormattedClinicalObservation[]>
    );

    const pinnedGroups = this.clinicalObsLocalStorageService.getPinnedGroups();

    const groupedByTypeArray = Object.keys(groupedByType).map((key) => {
      return {
        type: key,
        loinc_code: groupedByType[key][0].loinc_code || '',
        entries: groupedByType[key],
        isPinned: pinnedGroups.includes(key),
      } as ClinicalObservationGroup;
    });

    const getLatestDate = (group: ClinicalObservationGroup) =>
      group.entries.length > 0
        ? group.entries.map((r) => r.recorded_at).sort((a, b) => b.localeCompare(a))[0]
        : '';

    const pinned = groupedByTypeArray
      .filter((g) => g.isPinned)
      .sort((a, b) => getLatestDate(b).localeCompare(getLatestDate(a)));
    const unpinned = groupedByTypeArray
      .filter((g) => !g.isPinned)
      .sort((a, b) => getLatestDate(b).localeCompare(getLatestDate(a)));

    return [...pinned, ...unpinned];
  }
}
